import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import TimeOverviewGraph from './TimeOverviewGraph';
import LeaveModal from './LeaveModal';
import { 
  useAssignedTasks,
  useProjects,
  useTaskStatuses,
  useCalendarStatus,
  useUpcomingMeetings,
  useRecentActivity,
  useNotesStats,
  useCurrentLeave,
  useLeaveBalance,
  useMemberStats,
  useSystemSettings
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import {
  Clock,
  Calendar,
  AlertTriangle,
  Tag,
  Eye,
  Play,
  Square,
  CheckSquare,
  User,
  FolderOpen,
  BarChart3,
  Target,
  Activity,
  Video,
  MapPin,
  Users,
  Plus,
  StickyNote,
  TrendingUp,
  Umbrella,
  Home,
  List,
  PieChart,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
  Minimize2,
  Maximize2,
  CalendarDays
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import TaskDetail from './TaskDetail';
import Analytics from './Analytics';
import { useNavigate } from 'react-router-dom';
import { toUTCPlus5, toUTCPlus5DateOnly, toUTCPlus5DateTime, formatDueDate } from '../utils/time';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import ResponsiveContainer, { ResponsiveHeading, ResponsiveText, ResponsiveCard, ResponsiveButton, ResponsiveGrid } from './ResponsiveContainer';
import LocationPermission from './LocationPermission';
import StreakCounter from './StreakCounter';
import Skeleton from './Skeleton';
import Avatar from './Avatar';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: systemSettings } = useSystemSettings();
  const weeklyTarget = systemSettings?.weekly_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.weekly_target_hours;
  const annualLeaveLimit = systemSettings?.annual_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.annual_leave_limit;
  const casualLeaveLimit = systemSettings?.casual_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.casual_leave_limit;
  const medicalLeaveLimit = systemSettings?.medical_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.medical_leave_limit;
  const combinedLeavePoolLimit = Math.max(casualLeaveLimit, medicalLeaveLimit);
  const { activeTimer, startTimer: contextStartTimer, stopTimer: contextStopTimer, elapsedTime } = useTimer();
  
  // UI State (not data state) - moved before hook calls to avoid temporal dead zone
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [meetingsLastUpdated, setMeetingsLastUpdated] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    recentActivity: false,
    notesOverview: false,
    pastTasks: false,
    timeOverview: false,
    upcomingMeetings: false,
    todaysPlan: false
  });
  
  const { 
    data: tasksData, 
    isLoading: tasksLoading,
    refetch: fetchAssignedTasks
  } = useAssignedTasks();
  
  const { 
    data: projectsData, 
    isLoading: projectsLoading 
  } = useProjects();
  
  const { 
    data: statusesData, 
    isLoading: statusesLoading 
  } = useTaskStatuses();
  
  const { 
    data: calendarStatusData, 
    isLoading: calendarLoading 
  } = useCalendarStatus();

  const outlookConnected = calendarStatusData?.data?.connected || false;
  
  const { 
    data: meetingsData, 
    isLoading: meetingsLoading 
  } = useUpcomingMeetings({ enabled: outlookConnected });
  
  const { 
    data: activityData, 
    isLoading: activityLoading 
  } = useRecentActivity();
  
  const { 
    data: notesStatsData, 
    isLoading: notesLoading 
  } = useNotesStats();
  
  const { 
    data: currentLeaveData, 
    isLoading: currentLeaveLoading,
    refetch: checkCurrentLeave
  } = useCurrentLeave(user);
  
  const { 
    data: leaveBalanceData, 
    isLoading: leaveBalanceLoading 
  } = useLeaveBalance();
  
  const { 
    data: memberStatsData, 
    isLoading: memberStatsLoading,
    refetch: fetchMemberStats
  } = useMemberStats(selectedPeriod);
  
  // Extract data from React Query responses
  const tasks = tasksData?.results || [];
  const projects = projectsData?.results || [];
  const taskStatuses = statusesData || [];
  const upcomingMeetings = Array.isArray(meetingsData)
    ? meetingsData
    : (meetingsData?.events || []);
  const recentActivity = activityData || [];
  const notesStats = notesStatsData || null;
  const currentLeave = currentLeaveData || null;
  const leaveBalance = leaveBalanceData || null;
  const memberStats = memberStatsData || null;
  

  // 🎯 NO MORE useEffect FOR DATA FETCHING! React Query handles it automatically
  
  // Only keep useEffect for timer and UI interactions

  // Auto-refresh Outlook events every 5 minutes
  useEffect(() => {
    if (outlookConnected) {
      // Initial fetch
      fetchUpcomingMeetings();
      
      // Set up auto-refresh every 5 minutes
      const interval = setInterval(() => {
        fetchUpcomingMeetings();
      }, 5 * 60 * 1000); // 5 minutes
      
      return () => clearInterval(interval);
    }
  }, [outlookConnected]);

  // Note: useMemberStats automatically refetches when selectedPeriod changes
  // No manual refetch needed due to React Query's automatic dependency tracking


  const checkOutlookConnection = async () => {
    // Outlook connection status is now managed by React Query
    // No need to manually set state
  };

  const fetchUpcomingMeetings = async () => {
    // Upcoming meetings are now managed by React Query
    // No need to manually fetch or set state
  };

  // All data fetching is now handled by React Query hooks
  // No need for manual fetch functions

  const formatActivityMessage = (activity) => {
    const { action_type, table_name, details } = activity;
    
    switch (action_type) {
      case 'TASK_CREATED':
        return `Task "${details?.task_name || 'New Task'}" created`;
      case 'TASK_UPDATED':
        return `Task "${details?.task_name || 'Task'}" updated`;
      case 'TASK_STATUS_CHANGED':
        return `Task "${details?.task_name || 'Task'}" status changed to ${details?.new_status || 'Updated'}`;
      case 'TASK_ASSIGNED':
        return `Task "${details?.task_name || 'Task'}" assigned to you`;
      case 'TIME_ENTRY_CREATED':
        return `Time entry created for task "${details?.task_name || 'Task'}"`;
      case 'TIMER_STARTED':
        return `Timer started for task "${details?.task_name || 'Task'}"`;
      case 'TIMER_STOPPED':
        return `Timer stopped for task "${details?.task_name || 'Task'}"`;
      case 'PROJECT_CREATED':
        return `Project "${details?.project_name || 'New Project'}" created`;
      case 'PROJECT_UPDATED':
        return `Project "${details?.project_name || 'Project'}" updated`;
      case 'NOTE_CREATED':
        if (details?.note_type === 'task') {
          return `Note created on task "${details?.task_name || 'Task'}"`;
        } else if (details?.note_type === 'project') {
          return `Note created on project "${details?.project_name || 'Project'}"`;
        }
        return `Note created`;
      case 'NOTE_UPDATED':
        if (details?.note_type === 'task') {
          return `Note updated on task "${details?.task_name || 'Task'}"`;
        } else if (details?.note_type === 'project') {
          return `Note updated on project "${details?.project_name || 'Project'}"`;
        }
        return `Note updated`;
      case 'NOTE_DELETED':
        if (details?.note_type === 'task') {
          return `Note deleted from task "${details?.task_name || 'Task'}"`;
        } else if (details?.note_type === 'project') {
          return `Note deleted from project "${details?.project_name || 'Project'}"`;
        }
        return `Note deleted`;
      case 'TASK_SCREENSHOT_UPLOADED':
        return `Screenshot uploaded for task "${details?.task_name || 'Task'}"`;
      case 'TASK_SCREENSHOT_DELETED':
        return `Screenshot removed from task "${details?.task_name || 'Task'}"`;
      case 'TASK_COMMENT_ADDED':
        return `Comment added to task "${details?.task_name || 'Task'}"`;
      case 'TASK_COMMENT_UPDATED':
        return `Comment updated on task "${details?.task_name || 'Task'}"`;
      case 'TASK_COMMENT_DELETED':
        return `Comment removed from task "${details?.task_name || 'Task'}"`;
      case 'LEAVE_CREATED':
        return `Leave Created`;
      case 'LEAVE_UPDATED':
        return `Leave Updated`;
      case 'LEAVE_DELETED':
        return `Leave Deleted`;
      case 'USER_LOGIN':
        return `User login`;
      case 'USER_LOGOUT':
        return `User logout`;
      default:
        return `${action_type.replace(/_/g, ' ').toLowerCase()} on ${table_name}`;
    }
  };

  const getActivityIcon = (action_type) => {
    switch (action_type) {
      case 'TASK_CREATED':
      case 'TASK_UPDATED':
      case 'TASK_STATUS_CHANGED':
      case 'TASK_ASSIGNED':
      case 'TASK_COMMENT_ADDED':
      case 'TASK_COMMENT_UPDATED':
      case 'TASK_COMMENT_DELETED':
      case 'TASK_SCREENSHOT_UPLOADED':
      case 'TASK_SCREENSHOT_DELETED':
        return <CheckSquare className="h-4 w-4 text-green-600" />;
      case 'TIME_ENTRY_CREATED':
      case 'TIMER_STARTED':
      case 'TIMER_STOPPED':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'PROJECT_CREATED':
      case 'PROJECT_UPDATED':
        return <FolderOpen className="h-4 w-4 text-purple-600" />;
      case 'NOTE_CREATED':
      case 'NOTE_UPDATED':
      case 'NOTE_DELETED':
        return <Activity className="h-4 w-4 text-yellow-600" />;
      case 'LEAVE_CREATED':
      case 'LEAVE_UPDATED':
      case 'LEAVE_DELETED':
        return <Calendar className="h-4 w-4 text-teal-600" />;
      default:
        return <Activity className="h-4 w-4 text-secondary-600" />;
    }
  };

  const handleActivityClick = (activity) => {
    const { table_name, record_id, action_type } = activity;
    
    switch (table_name) {
      case 'tasks':
        navigate(`/tasks/${record_id}`);
        break;
      case 'projects':
        navigate(`/projects/${record_id}`);
        break;
      case 'time_entries':
        navigate(`/time-entries`);
        break;
      default:
        // Navigate to the main page for that table
        navigate(`/${table_name.replace('_', '-')}`);
    }
  };

  const formatTimeAgo = (timestamp) => {
    return toUTCPlus5DateTime(timestamp);
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const startTimer = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const success = await contextStartTimer(task.project_id, `Working on: ${task.name}`, taskId);
    if (success) fetchAssignedTasks();
  };

  const stopTimer = async () => {
    const success = await contextStopTimer();
    if (success) fetchAssignedTasks();
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200';
      case 'high': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200';
      case 'low': return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
      default: return 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="h-4 w-4" />;
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      case 'low': return <Calendar className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return { status: 'no-due-date', color: 'text-secondary-500' };
    
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { status: 'overdue', color: 'text-red-600' };
    if (diffDays === 0) return { status: 'due-today', color: 'text-orange-600' };
    if (diffDays <= 3) return { status: 'due-soon', color: 'text-yellow-600' };
    return { status: 'on-track', color: 'text-green-600' };
  };

  const getProjectName = (projectId) => {
    if (!Array.isArray(projects)) return 'Unknown Project';
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  const getStatusColor = (statusName) => {
    const status = taskStatuses.find(s => s.name === statusName);
    return status ? status.color : 'bg-gray-100 text-gray-800';
  };

  // Filter tasks assigned to the current user (exclude deleted tasks)
  const myAssignedTasks = tasks.filter(task => 
    !task.is_deleted && 
    Array.isArray(task.assigned_to) && 
    task.assigned_to.includes(user.id)
  );

  // Filter for dashboard cards
  const myActiveTasks = myAssignedTasks.filter(task => task.status !== 'Done');
  const myCompletedTasks = myAssignedTasks.filter(task => task.status === 'Done');
  const myCompletedLateTasks = myAssignedTasks.filter(task => task.completed_late);

  // For lists — treat anything not Done/Completed as active (matches QuickAccess).
  // Custom statuses (e.g. In QA) still count as assigned work the user owns.
  const activeTasks = myAssignedTasks.filter(
    (task) => !['Done', 'Completed'].includes(task.status)
  );
  const pastTasks = myAssignedTasks.filter((task) =>
    ['Done', 'Completed'].includes(task.status)
  );

  const isSameCalendarDay = (dateLike, day = new Date()) => {
    if (!dateLike) return false;
    const d = new Date(dateLike);
    return d.toDateString() === day.toDateString();
  };
  const tasksDueToday = activeTasks.filter((task) => isSameCalendarDay(task.due_date));
  const highPriorityTasks = activeTasks.filter(
    (task) => task.priority_level === 'urgent' || task.priority_level === 'high'
  );
  const meetingsToday = (upcomingMeetings || []).filter((meeting) =>
    isSameCalendarDay(meeting.start_time)
  );
  const hoursLoggedLabel =
    memberStats?.periodHours > 0
      ? `${memberStats.periodHours.toFixed(1)}h logged ${
          selectedPeriod === 'today' ? 'today' : selectedPeriod === 'week' ? 'this week' : 'this period'
        }`
      : 'No time logged yet';

  const formatMeetingTime = (dateTime) => {
    if (!dateTime) return '';
    try {
      const date = new Date(dateTime);
      // Convert to UTC+5 (Asia/Karachi timezone)
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Karachi',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      // Fallback to original format if timezone conversion fails
      const date = new Date(dateTime);
      return format(date, 'MMM dd, h:mm a');
    }
  };

  const getMeetingIcon = (event) => {
    if (event.location && event.location.toLowerCase().includes('teams')) {
      return <Video className="h-4 w-4 text-blue-600" />;
    }
    if (event.location && event.location.toLowerCase().includes('zoom')) {
      return <Video className="h-4 w-4 text-purple-600" />;
    }
    if (event.location) {
      return <MapPin className="h-4 w-4 text-green-600" />;
    }
    return <Calendar className="h-4 w-4 text-secondary-600" />;
  };

  // Don't block the UI with a global loader - let individual components handle their own loading states

  return (
    <>
      {/* Show Analytics for Admins only, Member Dashboard for Members and Project Leads */}
      {(user?.role === 'admin') ? (
        <Analytics />
      ) : (
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-12">
          {/* Location Permission Request */}
          <LocationPermission 
            onLocationObtained={async (locationData) => {
              // Location is already logged during login, no need to log again
            }}
            onLocationDenied={(error) => {
            }}
          />
          
          {/* Header Row: Time-based greeting + Hero metric + Selectors */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Avatar
                  blobName={user?.avatar}
                  alt={user?.full_name || user?.username || 'User'}
                  size="xl"
                  className="w-14 h-14 lg:w-16 lg:h-16 !rounded-2xl"
                  fallbackText={(user?.full_name || user?.username || '?').charAt(0).toUpperCase()}
                  previewable
                />
              </div>
              <div>
                <h1 className="text-xl lg:text-3xl font-bold text-secondary-900 dark:text-white mb-1">
                  {(() => {
                    const hour = new Date().getHours();
                    const firstName = user?.full_name?.split(' ')[0] || 'User';
                    if (hour < 12) return `Good morning, ${firstName}! 🌅`;
                    if (hour < 17) return `Good afternoon, ${firstName}! 🌞`;
                    return `Good evening, ${firstName}! 🌆`;
                  })()}
                </h1>
                <p className="text-secondary-600 dark:text-secondary-400 text-sm lg:text-base mb-3">
                  {memberStatsLoading 
                    ? 'Loading your stats...'
                    : (memberStats?.periodHours ?? 0) > 0 
                      ? `You've logged ${(memberStats.periodHours || 0).toFixed(1)}h this ${selectedPeriod === 'today' ? 'day' : selectedPeriod === 'week' ? 'week' : 'period'}. Keep it up!`
                      : 'Start your timer to track your first hours today.'}
                </p>
                {/* Weekly progress bar - only show when period is week */}
                {selectedPeriod === 'week' && memberStats && (
                  <div className="flex items-center gap-3 max-w-xs">
                    <div className="flex-1 h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 dark:bg-green-600 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, ((memberStats.periodHours || 0) / (memberStats.targetHours || weeklyTarget)) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-secondary-600 dark:text-secondary-400 whitespace-nowrap">
                      {(memberStats.periodHours || 0).toFixed(1)}h / {(memberStats.targetHours || weeklyTarget)}h
                    </span>
                  </div>
                )}
              </div>
            </div>
                        <div className="flex items-center gap-3 mt-2 lg:mt-0">
              {(user?.role === 'member' || user?.role === 'project_lead') && (
                <button
                  onClick={() => setShowLeaveModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-primary-100 dark:bg-primary-900/40 border border-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/60 transition-colors text-primary-700 dark:text-primary-300 font-medium text-sm"
                >
                  <Calendar className="h-4 w-4" />
                  Mark Leave
                </button>
              )}
              <select
                className="px-3 py-2 border border-primary-300 dark:border-primary-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm lg:text-base bg-white dark:bg-secondary-800 dark:text-white"
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value)}
              >
                <optgroup label="Quick Select">
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </optgroup>
                <optgroup label="Compare">
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="quarter">This Quarter</option>
                </optgroup>
                <optgroup label="Rolling Periods">
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_90_days">Last 90 Days</option>
                </optgroup>
              </select>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="border-b border-secondary-200 dark:border-secondary-700 mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Overview
                </div>
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'tasks'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Tasks
                </div>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'analytics'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <PieChart className="h-4 w-4" />
                  Analytics
                </div>
              </button>
              <button
                onClick={() => setActiveTab('leaves')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'leaves'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Leaves
                </div>
              </button>
              <button
                onClick={() => setActiveTab('calendar')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'calendar'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:border-secondary-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Calendar
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Member Stats Cards */}
            {(memberStats || true) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-4">
                <StreakCounter />
                {memberStats && (
                  <>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 lg:p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/time-entries')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs lg:text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">
                        My Hours {selectedPeriod === 'today' ? 'Today' : 
                         selectedPeriod === 'week' ? 'This Week' :
                         selectedPeriod === 'last_week' ? 'Last Week' :
                         selectedPeriod === 'month' ? 'This Month' :
                         selectedPeriod === 'last_month' ? 'Last Month' :
                         selectedPeriod === 'quarter' ? 'This Quarter' :
                         selectedPeriod === 'last_30_days' ? 'Last 30 Days' :
                         selectedPeriod === 'last_90_days' ? 'Last 90 Days' :
                         'This Period'}
                      </p>
                      <h2 className="text-lg lg:text-2xl font-bold text-secondary-900 dark:text-white">
                        {memberStats.periodHours ? memberStats.periodHours.toFixed(1) : '0'}h
                      </h2>
                    </div>
                    <div className="p-2 lg:p-3 bg-blue-100 dark:bg-blue-900/40 rounded-full">
                      <Clock className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 lg:p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tasks?filter=active')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs lg:text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">Active Tasks</p>
                      <h2 className="text-lg lg:text-2xl font-bold text-secondary-900 dark:text-white">
                        {myActiveTasks.length}
                      </h2>
                    </div>
                    <div className="p-2 lg:p-3 bg-green-100 dark:bg-green-900/40 rounded-full">
                      <Target className="h-4 w-4 lg:h-5 lg:w-5 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 lg:p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tasks?filter=completed')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs lg:text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">Completed Tasks</p>
                      <h2 className="text-lg lg:text-2xl font-bold text-secondary-900 dark:text-white">
                        {myCompletedTasks.length}
                      </h2>
                    </div>
                    <div className="p-2 lg:p-3 bg-purple-100 dark:bg-purple-900/40 rounded-full">
                      <CheckSquare className="h-4 w-4 lg:h-5 lg:w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 lg:p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tasks?filter=overdue')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs lg:text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">Overdue Tasks</p>
                      <h2 className="text-lg lg:text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {memberStats.overdueTasks || 0}
                      </h2>
                    </div>
                    <div className="p-2 lg:p-3 bg-orange-100 dark:bg-orange-900/40 rounded-full">
                      <AlertTriangle className="h-4 w-4 lg:h-5 lg:w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-3 lg:p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/tasks?filter=late')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs lg:text-sm font-medium text-secondary-600 dark:text-secondary-400 mb-1">Tasks Completed Late</p>
                      <h2 className="text-lg lg:text-2xl font-bold text-rose-600 dark:text-rose-400">
                        {myCompletedLateTasks.length}
                      </h2>
                    </div>
                    <div className="p-2 lg:p-3 bg-rose-100 dark:bg-rose-900/40 rounded-full">
                      <AlertTriangle className="h-4 w-4 lg:h-5 lg:w-5 text-rose-600 dark:text-rose-400" />
                    </div>
                  </div>
                </div>
                  </>
                )}
              </div>
            )}

            {/* Quick Start — one-click timer on assigned tasks */}
            {(user?.role === 'member' || user?.role === 'project_lead') && activeTasks.length > 0 && !activeTimer && (
              <div className="card overflow-hidden p-0">
                <div className="px-5 py-4 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                      <Play className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      Quick Start
                    </h3>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                      Start a timer on an assigned task
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/time-entries')}
                    className="btn-secondary text-sm inline-flex items-center gap-1.5 self-start sm:self-auto"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    Log time
                  </button>
                </div>
                <ul className="divide-y divide-secondary-100 dark:divide-secondary-700">
                  {activeTasks.slice(0, 5).map((task) => (
                    <li key={task.id} className="flex items-center gap-3 px-5 py-3 hover:bg-secondary-50/80 dark:hover:bg-secondary-700/40 transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {task.name}
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                          {getProjectName(task.project_id)}
                          {task.status ? ` · ${task.status}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => startTimer(task.id)}
                        className="btn-primary text-sm inline-flex items-center gap-1.5 flex-shrink-0"
                        title={`Start timer on ${task.name}`}
                      >
                        <Play className="h-3.5 w-3.5" />
                        Start
                      </button>
                    </li>
                  ))}
                </ul>
                {activeTasks.length > 5 && (
                  <div className="px-5 py-3 border-t border-secondary-100 dark:border-secondary-700">
                    <button
                      type="button"
                      onClick={() => navigate('/tasks')}
                      className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      View all {activeTasks.length} active tasks
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Today's Plan — members & project leads */}
            {(user?.role === 'member' || user?.role === 'project_lead') && (
              <div className="card overflow-hidden p-0">
                <div className="px-5 py-4 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                      <CalendarDays className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      Today
                    </h2>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                      {new Date().toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSection('todaysPlan')}
                    className="btn-secondary text-sm inline-flex items-center gap-1.5 self-start sm:self-auto"
                  >
                    {collapsedSections.todaysPlan ? (
                      <>
                        <Maximize2 className="h-3.5 w-3.5" />
                        Expand
                      </>
                    ) : (
                      <>
                        <Minimize2 className="h-3.5 w-3.5" />
                        Minimize
                      </>
                    )}
                  </button>
                </div>

                {!collapsedSections.todaysPlan ? (
                  <div className="p-5 space-y-5">
                    {/* Snapshot metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/60 px-3 py-2.5">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Due today</p>
                        <p className="text-xl font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {tasksDueToday.length}
                        </p>
                      </div>
                      <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/60 px-3 py-2.5">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">High priority</p>
                        <p className="text-xl font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {highPriorityTasks.length}
                        </p>
                      </div>
                      <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/60 px-3 py-2.5">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Active tasks</p>
                        <p className="text-xl font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {activeTasks.length}
                        </p>
                      </div>
                      <div className="rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/60 px-3 py-2.5">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Time</p>
                        <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1 leading-snug">
                          {hoursLoggedLabel}
                        </p>
                      </div>
                    </div>

                    {/* Due today list */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
                          Due today
                        </h3>
                        <button
                          type="button"
                          onClick={() => navigate('/tasks')}
                          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          All tasks
                        </button>
                      </div>

                      {tasksDueToday.length > 0 ? (
                        <ul className="divide-y divide-secondary-100 dark:divide-secondary-700 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                          {tasksDueToday.slice(0, 5).map((task) => (
                            <li key={task.id}>
                              <button
                                type="button"
                                onClick={() => navigate(`/tasks/${task.id}`)}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors"
                              >
                                <CheckSquare className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                                    {task.name}
                                  </p>
                                  <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                                    {getProjectName(task.project_id)}
                                  </p>
                                </div>
                                <span
                                  className={`text-[11px] font-medium capitalize px-2 py-0.5 rounded-md flex-shrink-0 ${
                                    task.priority_level === 'urgent'
                                      ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                      : task.priority_level === 'high'
                                        ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                        : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300'
                                  }`}
                                >
                                  {task.priority_level || 'medium'}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="rounded-lg border border-dashed border-secondary-200 dark:border-secondary-600 px-4 py-6 text-center">
                          <p className="text-sm font-medium text-secondary-800 dark:text-secondary-200">
                            Nothing due today
                          </p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 mb-3">
                            {activeTasks.length > 0
                              ? `${activeTasks.length} active task${activeTasks.length === 1 ? '' : 's'} without a due date today`
                              : 'No active assigned tasks'}
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            <button type="button" onClick={() => navigate('/tasks')} className="btn-primary text-sm">
                              View tasks
                            </button>
                            <button type="button" onClick={() => navigate('/projects')} className="btn-secondary text-sm">
                              Projects
                            </button>
                          </div>
                        </div>
                      )}

                      {tasksDueToday.length > 5 && (
                        <button
                          type="button"
                          onClick={() => navigate('/tasks')}
                          className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          +{tasksDueToday.length - 5} more due today
                        </button>
                      )}
                    </div>

                    {/* Meetings */}
                    {outlookConnected && meetingsToday.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
                            Meetings
                          </h3>
                          <span className="text-xs text-secondary-500">{meetingsToday.length}</span>
                        </div>
                        <ul className="divide-y divide-secondary-100 dark:divide-secondary-700 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                          {meetingsToday.slice(0, 3).map((meeting) => (
                            <li
                              key={meeting.id}
                              className="flex items-center gap-3 px-3 py-2.5"
                            >
                              <Video className="h-4 w-4 text-secondary-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                                  {meeting.subject || 'Untitled meeting'}
                                </p>
                                <p className="text-xs text-secondary-500">
                                  {formatMeetingTime(meeting.start_time)}
                                  {meeting.location ? ` · ${meeting.location}` : ''}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Lead shortcuts */}
                    {user?.role === 'project_lead' && (
                      <div className="flex flex-wrap gap-2 pt-1 border-t border-secondary-100 dark:border-secondary-700">
                        <button
                          type="button"
                          onClick={() => navigate('/tasks')}
                          className="btn-secondary text-sm inline-flex items-center gap-1.5"
                        >
                          <List className="h-3.5 w-3.5" />
                          Manage tasks
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/team')}
                          className="btn-secondary text-sm inline-flex items-center gap-1.5"
                        >
                          <Users className="h-3.5 w-3.5" />
                          Team
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/projects')}
                          className="btn-secondary text-sm inline-flex items-center gap-1.5"
                        >
                          <FolderOpen className="h-3.5 w-3.5" />
                          Projects
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-5 py-4 flex items-center justify-between gap-3 text-sm text-secondary-500 dark:text-secondary-400">
                    <span>
                      {tasksDueToday.length} due · {highPriorityTasks.length} high priority · {hoursLoggedLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleSection('todaysPlan')}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      Expand
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* My Time Analytics - Charts (comes after Today's Plan) */}
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="p-4 lg:p-5 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    My Time Analytics
                  </h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Your logged hours by project, task, and over time</p>
                </div>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="text-sm text-secondary-600 dark:text-secondary-300 font-medium bg-secondary-50 dark:bg-secondary-700 px-3 py-2 rounded-lg border border-secondary-200 dark:border-secondary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_90_days">Last 90 Days</option>
                </select>
              </div>
              <div className="p-4 lg:p-5">
                <TimeOverviewGraph period={selectedPeriod} />
              </div>
            </div>
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-6">
                {/* Assigned Tasks Section */}
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                    <h2 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white">
                      My Assigned Tasks ({activeTasks.length})
                    </h2>
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-secondary-400" />
                      <span className="text-secondary-600 text-sm">
                        {activeTasks.length} active
                      </span>
                    </div>
                  </div>

                  {activeTasks.length === 0 ? (
                    <div className="text-center py-12 px-6 bg-secondary-50 dark:bg-secondary-800/50 rounded-xl border-2 border-dashed border-secondary-200 dark:border-secondary-700">
                      <div className="w-16 h-16 bg-secondary-100 dark:bg-secondary-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckSquare className="h-8 w-8 text-secondary-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                        No active tasks yet
                      </h3>
                      <p className="text-secondary-600 dark:text-secondary-400 text-sm mb-4 max-w-md mx-auto">
                        You haven't been assigned to any active tasks. Reach out to your project lead or check back later!
                      </p>
                      <button
                        onClick={() => navigate('/projects')}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Browse Projects
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                                              {activeTasks.map((task) => {
                          const dueDateStatus = getDueDateStatus(task.due_date);
                          const isActiveTimer = activeTimer && activeTimer.task_id === task.id;
                          
                          return (
                            <div 
                              key={task.id} 
                              className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                              onClick={() => navigate(`/tasks/${task.id}`)}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <CheckSquare className="h-4 w-4 text-primary-600 flex-shrink-0" />
                                    <h3 className="font-semibold text-secondary-900 text-base group-hover:text-primary-600 transition-colors">{task.name}</h3>
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                                      {getProjectName(task.project_id)}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getPriorityColor(task.priority_level || 'medium')}`}>
                                      {getPriorityIcon(task.priority_level || 'medium')}
                                      {task.priority_level || 'medium'}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status || 'To Do')}`}>
                                      {task.status || 'To Do'}
                                    </span>
                                    {task.completed_late && (
                                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        Completed Late
                                      </span>
                                    )}
                                  </div>
                                  
                                  {task.description && (
                                    <p className="text-secondary-600 mb-2 ml-6 text-sm">{task.description}</p>
                                  )}
                                  
                                  <div className="flex flex-wrap items-center gap-3 ml-6 text-sm text-secondary-500">
                                    {task.estimated_hours && (
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        <span>{task.estimated_hours}h estimated</span>
                                      </div>
                                    )}
                                    {task.due_date && (
                                      <div className={`flex items-center gap-1 ${dueDateStatus.color}`}>
                                        <Calendar className="h-3 w-3" />
                                        <span>
                                          Due: {task.due_date ? formatDueDate(task.due_date) : '—'}
                                          {dueDateStatus.status === 'overdue' && ' (Overdue)'}
                                          {dueDateStatus.status === 'due-today' && ' (Today)'}
                                        </span>
                                      </div>
                                    )}
                                    {task.tags && task.tags.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Tag className="h-3 w-3" />
                                        <span>{task.tags.join(', ')}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2 self-end">
                                  <div className="p-2 text-secondary-400 group-hover:text-primary-600 transition-colors">
                                    <Eye className="h-4 w-4" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>

                
                {/* Past Tasks Section */}
                {pastTasks.length > 0 && (
                  <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white">
                        Past Tasks ({pastTasks.length})
                      </h2>
                      <button
                        onClick={() => toggleSection('pastTasks')}
                        className="flex items-center gap-2 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                      >
                        {collapsedSections.pastTasks ? (
                          <>
                            <Maximize2 className="h-4 w-4" />
                            <span>Expand</span>
                          </>
                        ) : (
                          <>
                            <Minimize2 className="h-4 w-4" />
                            <span>Minimize</span>
                          </>
                        )}
                      </button>
                    </div>
                    {!collapsedSections.pastTasks && (
                    <div className="space-y-3">
                                              {pastTasks.map((task) => {
                          const dueDateStatus = getDueDateStatus(task.due_date);
                          const isActiveTimer = activeTimer && activeTimer.task_id === task.id;
                          
                          return (
                            <div 
                              key={task.id} 
                              className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                              onClick={() => navigate(`/tasks/${task.id}`)}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <CheckSquare className="h-4 w-4 text-primary-600 flex-shrink-0" />
                                    <h3 className="font-semibold text-secondary-900 text-base group-hover:text-primary-600 transition-colors">{task.name}</h3>
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                                      {getProjectName(task.project_id)}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getPriorityColor(task.priority_level || 'medium')}`}>
                                      {getPriorityIcon(task.priority_level || 'medium')}
                                      {task.priority_level || 'medium'}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status || 'To Do')}`}>
                                      {task.status || 'To Do'}
                                    </span>
                                    {task.completed_late && (
                                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        Completed Late
                                      </span>
                                    )}
                                  </div>
                                  
                                  {task.description && (
                                    <p className="text-secondary-600 mb-2 ml-6 text-sm">{task.description}</p>
                                  )}
                                  
                                  <div className="flex flex-wrap items-center gap-3 ml-6 text-sm text-secondary-500">
                                    {task.estimated_hours && (
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        <span>{task.estimated_hours}h estimated</span>
                                      </div>
                                    )}
                                    {task.due_date && (
                                      <div className={`flex items-center gap-1 ${dueDateStatus.color}`}>
                                        <Calendar className="h-3 w-3" />
                                        <span>
                                          Due: {task.due_date ? formatDueDate(task.due_date) : '—'}
                                          {dueDateStatus.status === 'overdue' && ' (Overdue)'}
                                          {dueDateStatus.status === 'due-today' && ' (Today)'}
                                        </span>
                                      </div>
                                    )}
                                    {task.tags && task.tags.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Tag className="h-3 w-3" />
                                        <span>{task.tags.join(', ')}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2 self-end">
                                  <div className="p-2 text-secondary-400 group-hover:text-primary-600 transition-colors">
                                    <Eye className="h-4 w-4" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {collapsedSections.pastTasks && (
                      <div className="text-center py-4">
                        <div className="flex items-center justify-center gap-2 text-secondary-500">
                          <ChevronRight className="h-4 w-4" />
                          <span className="text-sm">Past Tasks minimized</span>
                    </div>
                  </div>
                )}
              </div>
                )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
              <div className="space-y-6">
              {/* My Time Analytics - Full charts for logged-in user */}
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <div className="p-4 lg:p-5 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      My Time Analytics
                    </h2>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">Your logged hours — by project, task, category, and daily trend</p>
                  </div>
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="text-sm text-secondary-600 dark:text-secondary-300 font-medium bg-secondary-50 dark:bg-secondary-700 px-3 py-2 rounded-lg border border-secondary-200 dark:border-secondary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="last_week">Last Week</option>
                    <option value="month">This Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="quarter">This Quarter</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="last_90_days">Last 90 Days</option>
                  </select>
                </div>
                <div className="p-4 lg:p-5">
                  <TimeOverviewGraph period={selectedPeriod} />
                </div>
              </div>

              {/* Summary Stats Row */}
              {memberStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-4">
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Hours Logged</p>
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1">{(memberStats.periodHours || 0).toFixed(1)}h</p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">This {selectedPeriod === 'today' ? 'day' : selectedPeriod === 'week' ? 'week' : 'period'}</p>
                  </div>
                  <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-4">
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Active Tasks</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{myActiveTasks.length}</p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">In progress</p>
                  </div>
                  <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-4">
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Completed</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{myCompletedTasks.length}</p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Tasks done</p>
                  </div>
                  <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-4">
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">Overdue</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{memberStats.overdueTasks || 0}</p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Need attention</p>
                  </div>
                </div>
              )}

              {/* Recent Activity Card */}
                  <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white">Recent Activity</h2>
                      <button
                      onClick={() => toggleSection('recentActivity')}
                      className="flex items-center gap-2 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                    >
                      {collapsedSections.recentActivity ? (
                        <>
                          <Maximize2 className="h-4 w-4" />
                          <span>Expand</span>
                        </>
                      ) : (
                        <>
                          <Minimize2 className="h-4 w-4" />
                          <span>Minimize</span>
                                    </>
                                  )}
                        </button>
                      </div>
                  </div>
                {!collapsedSections.recentActivity && (
                  <>
                  <div className="space-y-3">
                    {activityLoading ? (
                      <div className="space-y-3 py-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center gap-3">
                            <Skeleton className="h-8 w-8 flex-shrink-0" variant="circular" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-3/4" variant="text" />
                              <Skeleton className="h-3 w-1/4" variant="text" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : recentActivity.length === 0 ? (
                      <div className="text-center py-10 px-6 bg-secondary-50 dark:bg-secondary-800/50 rounded-2xl border border-dashed border-secondary-200 dark:border-secondary-700">
                        <Activity className="h-12 w-12 text-secondary-400 dark:text-secondary-500 mx-auto mb-3" />
                        <p className="text-secondary-600 dark:text-secondary-400 text-sm font-medium">No recent activity</p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">Start logging time or working on tasks to see your activity here</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                          {recentActivity.slice(0, 5).map((activity) => (
                          <div
                            key={activity.id}
                            className="flex items-center gap-3 p-2 rounded-lg"
                          >
                            <div className="flex-shrink-0">
                              {getActivityIcon(activity.action_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-secondary-900 dark:text-white truncate">
                                {formatActivityMessage(activity)}
                              </p>
                              <p className="text-xs text-secondary-500">
                                {formatTimeAgo(activity.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-secondary-100">
                    <button
                      onClick={() => navigate('/my-activity')}
                      className="w-full text-center text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
                    >
                      View All Activity →
                    </button>
                  </div>
                  </>
                )}
                {collapsedSections.recentActivity && (
                  <div className="text-center py-4">
                    <div className="flex items-center justify-center gap-2 text-secondary-500">
                      <ChevronRight className="h-4 w-4" />
                      <span className="text-sm">Recent Activity minimized</span>
                    </div>
                  </div>
                )}
                </div>

                {/* Notes Stats Widget */}
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                  <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                      <StickyNote className="h-5 w-5 text-primary-600" />
                      Notes Overview
                    </h2>
                    <button
                      onClick={() => toggleSection('notesOverview')}
                      className="flex items-center gap-2 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                    >
                      {collapsedSections.notesOverview ? (
                        <>
                          <Maximize2 className="h-4 w-4" />
                          <span>Expand</span>
                        </>
                      ) : (
                        <>
                          <Minimize2 className="h-4 w-4" />
                          <span>Minimize</span>
                        </>
                      )}
                    </button>
                  </div>
                  </div>
                  
                {!collapsedSections.notesOverview && (
                  <>
                  {notesLoading ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <Skeleton className="h-16" />
                        <Skeleton className="h-16" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-1/3" variant="text" />
                        <Skeleton className="h-12" />
                        <Skeleton className="h-12" />
                      </div>
                    </div>
                  ) : !notesStats ? (
                    <div className="text-center py-10 px-6 bg-secondary-50 dark:bg-secondary-800/50 rounded-2xl border border-dashed border-secondary-200 dark:border-secondary-700">
                      <StickyNote className="h-12 w-12 text-secondary-400 dark:text-secondary-500 mx-auto mb-3" />
                      <p className="text-secondary-600 dark:text-secondary-400 text-sm font-medium">No notes yet</p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">Add notes to tasks to keep track of your progress</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Notes Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-primary-600 dark:text-primary-400">{notesStats.total_notes}</div>
                          <div className="text-xs text-primary-700 dark:text-primary-300">Total Notes</div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-green-600 dark:text-green-400">{notesStats.pending_tasks_with_notes}</div>
                          <div className="text-xs text-green-700 dark:text-green-300">Active Tasks with Notes</div>
                        </div>
                      </div>
                      
                      {/* Recent Task Notes */}
                      {notesStats.recent_task_notes && notesStats.recent_task_notes.length > 0 && (
                        <div>
                          <h3 className="text-sm font-medium text-secondary-900 mb-3 flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            Recent Task Notes
                          </h3>
                          <div className="space-y-2">
                            {notesStats.recent_task_notes.slice(0, 3).map((note) => (
                              <div key={note.id} className="bg-secondary-50 rounded-lg p-3 border border-secondary-200">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2">
                                    <div className="h-5 w-5 rounded-full bg-primary-100 flex items-center justify-center">
                                      <span className="text-xs font-bold text-primary-600">
                                        {note.created_by_name ? note.created_by_name[0].toUpperCase() : 'U'}
                                      </span>
                                    </div>
                                    <span className="text-xs font-medium text-secondary-900">{note.created_by_name}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {note.visibility === 'private' ? (
                                      <div className="h-2 w-2 bg-red-500 rounded-full" title="Private note" />
                                    ) : (
                                      <div className="h-2 w-2 bg-green-500 rounded-full" title="Public note" />
                                    )}
                                    <span className="text-xs text-secondary-500">
                                      {format(new Date(new Date(note.created_at).getTime() + 5 * 60 * 60 * 1000), 'MMM d')}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-xs text-secondary-700 line-clamp-2">
                                  {note.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Quick Stats Summary */}
                      <div className="border-t border-secondary-100 dark:border-secondary-700 pt-3">
                        <div className="flex justify-between text-xs text-secondary-600 dark:text-secondary-400">
                          <span>Completed tasks with notes: {notesStats.completed_tasks_with_notes}</span>
                          {user?.role === 'member' && (
                            <button
                              onClick={() => navigate('/projects')}
                              className="text-primary-600 hover:text-primary-700 font-medium"
                            >
                              View Projects →
                            </button>
                          )}
                        </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {collapsedSections.notesOverview && (
                  <div className="text-center py-4">
                    <div className="flex items-center justify-center gap-2 text-secondary-500 dark:text-secondary-400">
                      <ChevronRight className="h-4 w-4" />
                      <span className="text-sm">Notes Overview minimized</span>
                      </div>
                    </div>
                  )}
                </div>

            </div>
          )}

          {/* Leaves Tab */}
          {activeTab === 'leaves' && (
            <div className="space-y-6">
              {(user?.role === 'member' || user?.role === 'project_lead') && (
                <div className="card overflow-hidden p-0">
                  <div className="px-5 py-4 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                        <Umbrella className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        Leave balance
                      </h3>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                        Remaining days by category
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                      <button
                        type="button"
                        onClick={() => setShowLeaveModal(true)}
                        className="btn-primary text-sm inline-flex items-center gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Request leave
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/leave-management')}
                        className="btn-secondary text-sm inline-flex items-center gap-1.5"
                      >
                        <CalendarDays className="h-3.5 w-3.5" />
                        Manage
                      </button>
                    </div>
                  </div>

                  {leaveBalanceLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                    </div>
                  ) : leaveBalance ? (
                    (() => {
                      const rows = [
                        {
                          key: 'annual',
                          label: 'Annual',
                          hint: 'Vacation',
                          remaining: leaveBalance.annual_remaining,
                          limit: leaveBalance.annual_limit,
                        },
                        {
                          key: 'casual',
                          label: 'Casual',
                          hint: 'Personal',
                          remaining: leaveBalance.casual_remaining,
                          limit: leaveBalance.casual_limit,
                        },
                        {
                          key: 'medical',
                          label: 'Medical',
                          hint: 'Health',
                          remaining: leaveBalance.medical_remaining,
                          limit: leaveBalance.medical_limit,
                        },
                      ];
                      const totalLimit = (leaveBalance.annual_limit || annualLeaveLimit) + combinedLeavePoolLimit;
                      const totalPct = totalLimit
                        ? Math.min(100, Math.round((leaveBalance.total_remaining / totalLimit) * 100))
                        : 0;

                      return (
                        <>
                          <ul className="divide-y divide-secondary-100 dark:divide-secondary-700">
                            {rows.map((row) => {
                              const pct =
                                row.limit > 0
                                  ? Math.min(100, Math.round((row.remaining / row.limit) * 100))
                                  : 0;
                              return (
                                <li key={row.key} className="px-5 py-3.5">
                                  <div className="flex items-center justify-between gap-4 mb-2">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                                        {row.label}
                                      </p>
                                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                                        {row.hint}
                                      </p>
                                    </div>
                                    <p className="text-sm font-semibold text-secondary-900 dark:text-white tabular-nums flex-shrink-0">
                                      {row.remaining}
                                      <span className="text-secondary-400 font-normal"> / {row.limit}</span>
                                    </p>
                                  </div>
                                  <div className="h-1.5 w-full rounded-full bg-secondary-100 dark:bg-secondary-700 overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-primary-600 dark:bg-primary-500 transition-all"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="px-5 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50/70 dark:bg-secondary-800/50">
                            <div className="flex items-center justify-between gap-4 mb-2">
                              <div>
                                <p className="text-sm font-medium text-secondary-900 dark:text-white">
                                  Available total
                                </p>
                                <p className="text-xs text-secondary-500 dark:text-secondary-400">
                                  Annual plus shared casual/medical pool
                                </p>
                              </div>
                              <p className="text-sm font-semibold text-secondary-900 dark:text-white tabular-nums">
                                {leaveBalance.total_remaining}
                                <span className="text-secondary-400 font-normal"> / {totalLimit} days</span>
                              </p>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-secondary-200 dark:bg-secondary-700 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary-600 dark:bg-primary-500"
                                style={{ width: `${totalPct}%` }}
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="px-5 py-10 text-center">
                      <p className="text-sm text-secondary-600 dark:text-secondary-400">
                        Unable to load leave balance
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              {/* Upcoming Meetings Card - Only when Outlook is connected */}
              {outlookConnected && (
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg lg:text-xl font-semibold text-secondary-900 dark:text-white">Upcoming Meetings</h2>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleSection('upcomingMeetings')}
                        className="flex items-center gap-2 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                      >
                        {collapsedSections.upcomingMeetings ? (
                          <>
                            <Maximize2 className="h-4 w-4" />
                            <span>Expand</span>
                          </>
                        ) : (
                          <>
                            <Minimize2 className="h-4 w-4" />
                            <span>Minimize</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={fetchUpcomingMeetings}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>
                    
                  {!collapsedSections.upcomingMeetings && (
                    <>
                      {meetingsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        </div>
                      ) : upcomingMeetings.length === 0 ? (
                        <div className="text-center py-6">
                          <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                          <p className="text-secondary-600 text-sm">No upcoming meetings</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {upcomingMeetings.map((meeting) => (
                            <div key={meeting.id} className="border border-secondary-100 rounded-lg p-3 hover:bg-secondary-50 transition-colors">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-1">
                                  {getMeetingIcon(meeting)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-secondary-900 text-sm mb-1 truncate">
                                    {meeting.subject || 'No Subject'}
                                  </h3>
                                  <div className="flex items-center gap-2 text-xs text-secondary-600 mb-1">
                                    <Clock className="h-3 w-3" />
                                    <span>{formatMeetingTime(meeting.start_time)}</span>
                                    {meeting.end_time && (
                                      <>
                                        <span>-</span>
                                        <span>
                                          {(() => {
                                            try {
                                              const endDate = new Date(meeting.end_time);
                                              return endDate.toLocaleString('en-US', {
                                                timeZone: 'Asia/Karachi',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                              });
                                            } catch (error) {
                                              return format(new Date(meeting.end_time), 'h:mm a');
                                            }
                                          })()}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {meeting.location && (
                                    <div className="flex items-center gap-1 text-xs text-secondary-500">
                                      <MapPin className="h-3 w-3" />
                                      <span className="truncate">{meeting.location}</span>
                                    </div>
                                  )}
                                  {meeting.attendees && meeting.attendees.length > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-secondary-500 mt-1">
                                      <Users className="h-3 w-3" />
                                      <span>{meeting.attendees.length} attendee{meeting.attendees.length !== 1 ? 's' : ''}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {upcomingMeetings.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-secondary-100">
                          <button
                            onClick={() => navigate('/calendar')}
                            className="w-full text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
                          >
                            View All Meetings →
                          </button>
                          {meetingsLastUpdated && (
                            <p className="text-xs text-secondary-400 text-center mt-2">
                              Last updated: {meetingsLastUpdated.toLocaleString('en-US', {
                                timeZone: 'Asia/Karachi',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {collapsedSections.upcomingMeetings && (
                    <div className="text-center py-4">
                      <div className="flex items-center justify-center gap-2 text-secondary-500">
                        <ChevronRight className="h-4 w-4" />
                        <span className="text-sm">Upcoming Meetings minimized</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Outlook Connection Status - Show when not connected */}
              {!outlookConnected && (
                <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 lg:p-6">
                  <div className="text-center py-6">
                    <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-secondary-900 mb-2">Calendar Integration</h3>
                    <p className="text-secondary-600 text-sm mb-4">
                      Connect your Outlook calendar to view upcoming meetings and events.
                    </p>
                    <button
                      onClick={() => navigate('/calendar')}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Connect Calendar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <LeaveModal 
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onSuccess={() => {
          if (checkCurrentLeave) checkCurrentLeave(); // Refresh leave status (only for members/project leads)
          fetchMemberStats(selectedPeriod); // Refresh stats
        }}
      />
    </>
  );
};

export default Dashboard; 